import {
	compareDates,
	dayOfMonth,
	daysInMonth,
	endOfMonth,
	endOfWeek,
	startOfMonth,
	startOfWeek,
	weekday
} from "./dates";
import type { CompletionLike, PlainDate, Recurrence } from "./types";

/**
 * Движок повторений: активность на дату, границы периода, прогресс «x/N».
 * Материализации нет — копии на каждый день не создаются, всё считается на лету
 * (spec-tasks Область 6).
 */

export function recurrenceCount(rec: Recurrence): number {
	return rec.count ?? 1;
}

/**
 * «31-е в коротком месяце» → последний день месяца. Без пропусков:
 * пропуск месяца превратил бы «раз в месяц» в «10 раз в год».
 */
export function clampDayOfMonth(day: number, date: PlainDate): number {
	return Math.min(day, daysInMonth(date));
}

/**
 * Структурная активность на дату: матчит ли период саму дату.
 * Достигнутость цели здесь НЕ учитывается — её считает `progressOn`,
 * потому что панель повторяющихся показывает и то, и другое отдельно.
 */
export function isActiveOn(rec: Recurrence, date: PlainDate): boolean {
	switch (rec.period) {
		case "day": {
			if (!rec.daysOfWeek || rec.daysOfWeek.length === 0) {
				return true;
			}
			return rec.daysOfWeek.includes(weekday(date));
		}
		case "week":
			// Цель считается за неделю целиком — активна в любой её день.
			return true;
		case "month": {
			if (rec.dayOfMonth === undefined) {
				return true;
			}
			return dayOfMonth(date) === clampDayOfMonth(rec.dayOfMonth, date);
		}
	}
}

/** Границы периода, в который попадает дата. Обе включительно. */
export function periodBounds(rec: Recurrence, date: PlainDate): { from: PlainDate; to: PlainDate } {
	switch (rec.period) {
		case "day":
			return { from: date, to: date };
		case "week":
			return { from: startOfWeek(date), to: endOfWeek(date) };
		case "month":
			return { from: startOfMonth(date), to: endOfMonth(date) };
	}
}

export function isInPeriod(rec: Recurrence, date: PlainDate, on: PlainDate): boolean {
	const { from, to } = periodBounds(rec, date);
	return compareDates(on, from) >= 0 && compareDates(on, to) <= 0;
}

export type RecurrenceProgress = {
	done: number;
	target: number;
	/** Цель за период достигнута. */
	complete: boolean;
	from: PlainDate;
	to: PlainDate;
};

/** Прогресс «x из N» = сколько completions попало в границы периода, содержащего дату. */
export function progressOn(
	rec: Recurrence,
	date: PlainDate,
	completions: readonly CompletionLike[]
): RecurrenceProgress {
	const { from, to } = periodBounds(rec, date);
	const target = recurrenceCount(rec);
	let done = 0;
	for (const c of completions) {
		if (compareDates(c.completedOn, from) >= 0 && compareDates(c.completedOn, to) <= 0) {
			done += 1;
		}
	}
	return { done, target, complete: done >= target, from, to };
}

/** Форма `recurrence` валидна. Zod-схема в `core` проверяет то же — здесь предикат для клиента. */
export function validateRecurrence(rec: Recurrence): string | null {
	const count = rec.count;
	if (count !== undefined && (!Number.isInteger(count) || count < 1)) {
		return "count должен быть целым >= 1";
	}
	if (rec.daysOfWeek !== undefined) {
		if (rec.period !== "day") {
			return "daysOfWeek допустим только при period='day'";
		}
		if (rec.daysOfWeek.length === 0) {
			return "daysOfWeek не может быть пустым";
		}
		if (rec.daysOfWeek.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) {
			return "daysOfWeek — целые 0..6";
		}
		if (new Set(rec.daysOfWeek).size !== rec.daysOfWeek.length) {
			return "daysOfWeek не должен содержать повторов";
		}
	}
	if (rec.dayOfMonth !== undefined) {
		if (rec.period !== "month") {
			return "dayOfMonth допустим только при period='month'";
		}
		if (!Number.isInteger(rec.dayOfMonth) || rec.dayOfMonth < 1 || rec.dayOfMonth > 31) {
			return "dayOfMonth — целое 1..31";
		}
	}
	if (rec.times?.some((t) => !/^([01]\d|2[0-3]):[0-5]\d$/.test(t))) {
		return "times — строки вида HH:MM";
	}
	return null;
}
